import { AlertTriangle } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Alert, Button, Input, Modal, Select } from "@/components/common";
import {
  useAddContractBonus,
  useDeleteContractBonus,
} from "@/hooks/useReports";
import { useInsurers } from "@/hooks/useSettlements";
import type { ContractBonus } from "@/types/reports";
import { formatCurrency, MONTHS } from "./format";

/** Accepts "1 234,56" as well as "1234.56". */
function parseAmount(text: string): number {
  const normalized = text.replace(/\s/g, "").replace(",", ".");
  return /^\d+(\.\d{1,2})?$/.test(normalized) ? Number(normalized) : Number.NaN;
}

interface AddContractBonusModalProps {
  isOpen: boolean;
  onClose: () => void;
  year: number;
  /** Month to start with; the user can change it. */
  defaultMonth: number;
  yearOptions: { value: string; label: string }[];
}

export function AddContractBonusModal({
  isOpen,
  onClose,
  year: defaultYear,
  defaultMonth,
  yearOptions,
}: AddContractBonusModalProps) {
  const addBonus = useAddContractBonus();
  const { data: insurers } = useInsurers();

  const [year, setYear] = useState(defaultYear);
  const [month, setMonth] = useState(defaultMonth);
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [insurerId, setInsurerId] = useState("");
  // The modal stays mounted: pick up the period the page shows each time it opens.
  const [openedFor, setOpenedFor] = useState<string | null>(null);
  const key = isOpen ? `${defaultYear}-${defaultMonth}` : null;
  if (key !== openedFor) {
    setOpenedFor(key);
    if (isOpen) {
      setYear(defaultYear);
      setMonth(defaultMonth);
    }
  }

  const parsed = parseAmount(amount);
  const amountError =
    amount.trim() && !(parsed > 0)
      ? "Podaj kwotę większą od zera, np. 1500 lub 1500,50."
      : undefined;
  const valid = parsed > 0 && description.trim().length > 0;

  function handleClose() {
    setAmount("");
    setDescription("");
    setInsurerId("");
    addBonus.reset();
    onClose();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!valid) {
      return;
    }
    addBonus.mutate(
      {
        year,
        month,
        amount: parsed,
        description: description.trim(),
        insurerId: insurerId || null,
      },
      {
        onSuccess: (bonus) => {
          toast.success(
            `Dodano premię ${formatCurrency(bonus.amount, 2)} — ${MONTHS[bonus.month - 1]} ${bonus.year}.`,
          );
          handleClose();
        },
      },
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Premia / wypłata z kontraktu"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {addBonus.error && (
          <Alert variant="error">
            {(addBonus.error as { message?: string }).message ??
              "Nie udało się dodać premii"}
          </Alert>
        )}

        <p className="text-sm text-dim">
          Cała kwota trafia do zysku firmy — nie dzieli się jej z agentami.
        </p>

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Miesiąc"
            value={String(month)}
            onChange={(v) => {
              setMonth(Number(v));
            }}
            options={MONTHS.map((label, i) => ({
              value: String(i + 1),
              label,
            }))}
          />
          <Select
            label="Rok"
            value={String(year)}
            onChange={(v) => {
              setYear(Number(v));
            }}
            options={yearOptions}
          />
        </div>

        <Input
          label="Kwota (zł)"
          value={amount}
          onChange={(e) => {
            setAmount(e.target.value);
          }}
          inputMode="decimal"
          placeholder="np. 5000,00"
          required
          autoComplete="off"
          error={amountError}
        />

        <Input
          label="Opis"
          value={description}
          onChange={(e) => {
            setDescription(e.target.value);
          }}
          placeholder="np. Premia za realizację planu sprzedaży"
          required
          maxLength={200}
          autoComplete="off"
        />

        <Select
          label="Towarzystwo (opcjonalnie)"
          value={insurerId}
          onChange={setInsurerId}
          options={[
            { value: "", label: "— bez towarzystwa —" },
            ...(insurers ?? []).map((i) => ({ value: i.id, label: i.name })),
          ]}
        />

        <div className="flex justify-end gap-3 border-t border-edge-soft pt-4">
          <Button variant="ghost" type="button" onClick={handleClose}>
            Anuluj
          </Button>
          <Button
            type="submit"
            isLoading={addBonus.isPending}
            disabled={!valid}
          >
            Dodaj premię
          </Button>
        </div>
      </form>
    </Modal>
  );
}

interface DeleteContractBonusModalProps {
  bonus: ContractBonus | null;
  onClose: () => void;
}

export function DeleteContractBonusModal({
  bonus,
  onClose,
}: DeleteContractBonusModalProps) {
  const deleteBonus = useDeleteContractBonus();

  function handleClose() {
    deleteBonus.reset();
    onClose();
  }

  function handleConfirm() {
    if (!bonus) {
      return;
    }
    deleteBonus.mutate(bonus.id, {
      onSuccess: () => {
        toast.success("Usunięto premię.");
        handleClose();
      },
    });
  }

  return (
    <Modal isOpen={bonus !== null} onClose={handleClose} title="Usuń premię">
      <div className="space-y-4">
        {deleteBonus.isError && (
          <Alert variant="error">
            {(deleteBonus.error as { message?: string })?.message ??
              "Nie udało się usunąć premii"}
          </Alert>
        )}

        <div className="flex gap-3 rounded-lg bg-red-50 p-3 dark:bg-red-900/20">
          <AlertTriangle
            size={20}
            className="shrink-0 text-red-600 dark:text-red-400"
            aria-hidden="true"
          />
          <p className="text-sm text-red-800 dark:text-red-300">
            Premia{" "}
            <span className="font-semibold">
              {bonus?.description} ({bonus && formatCurrency(bonus.amount, 2)})
            </span>{" "}
            zniknie z wyników firmy za{" "}
            {bonus && `${MONTHS[bonus.month - 1]} ${bonus.year}`}.
          </p>
        </div>

        <div className="flex justify-end gap-3 border-t border-edge-soft pt-4">
          <Button variant="ghost" onClick={handleClose}>
            Anuluj
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirm}
            isLoading={deleteBonus.isPending}
          >
            Usuń premię
          </Button>
        </div>
      </div>
    </Modal>
  );
}
